import React, { useState, useEffect } from 'react';
import { doc, getDoc, getFirestore, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getStorage, deleteObject } from 'firebase/storage';
import { getAuth, updatePassword, updateEmail, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import firebaseApp from "../firebase/credenciales";
import { 
  FaUser, 
  FaEdit, 
  FaSave, 
  FaTimes, 
  FaCamera, 
  FaEye, 
  FaEyeSlash, 
  FaKey, 
  FaEnvelope,
  FaIdCard,
  FaBriefcase,
  FaBuilding,
  FaUserTie,
  FaShieldAlt,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaInfoCircle
} from 'react-icons/fa';

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

const Perfil = ({ forcePasswordChange = false, onPasswordChanged = null }) => {
  const [userData, setUserData] = useState({
    name: '',
    apellidos: '',
    email: '',
    dni: '',
    cargo: '',
    rol: '',
    rucEmpresa: '',
    supervisor: '',
    recursosHumanos: '',
    activo: true,
    lastActive: '',
    avatar: '',
    telefono: '',
    fechaNacimiento: '',
    direccion: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [previewAvatar, setPreviewAvatar] = useState('');

  // Estados para cambio de contraseña
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Estados para alertas
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const currentUser = auth.currentUser;

  // Roles disponibles para mostrar
  const roles = {
    superAdmin: 'Super Administrador',
    admin: 'Administrador',
    supervisor: 'Supervisor',
    asistencia: 'Asistencia',
    almacenero: 'Almacenero',
    rrhh: 'Recursos Humanos'
  };

  useEffect(() => {
    if (currentUser) {
      loadUserProfile();
    }
  }, [currentUser]);

  useEffect(() => {
    if (forcePasswordChange) {
      setShowPasswordForm(true);
      setIsEditing(false);
    }
  }, [forcePasswordChange]);

  // Función para mostrar alertas
  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

const loadUserProfile = async () => {
  try {
    setLoading(true);
    if (!currentUser) return;

    // Primero intentar con el UID de Firebase Auth
    let userDoc = await getDoc(doc(db, 'users', currentUser.uid));
    
    if (userDoc.exists()) {
      // Si existe con UID, usar esos datos
      const data = userDoc.data();
      setUserData(data);
      setPreviewAvatar(data.avatar || '');
      console.log('Usuario encontrado por UID:', currentUser.uid);
    } else {
      // Si no existe con UID, intentar buscar por email en toda la colección
      console.log('Usuario no encontrado por UID, buscando por email...');
      
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const q = query(collection(db, 'users'), where('email', '==', currentUser.email));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          // Si encontramos el usuario por email, usar esos datos
          const docSnap = querySnapshot.docs[0];
          const data = docSnap.data();
          const docId = docSnap.id; // Este será el DNI u otro identificador
          
          setUserData({
            ...data,
            documentId: docId // Guardar el ID del documento para futuras actualizaciones
          });
          setPreviewAvatar(data.avatar || '');
          console.log('Usuario encontrado por email con ID:', docId);
          showAlert('info', 'Usuario encontrado. Algunos datos podrían requerir migración.');
        } else {
          // Si no existe en ningún lado, mostrar mensaje
          showAlert('warning', 'No se encontró el perfil del usuario. Puede crear uno nuevo.');
        }
      } catch (queryError) {
        console.error('Error en búsqueda por email:', queryError);
        showAlert('warning', 'No se encontró el perfil del usuario');
      }
    }
  } catch (error) {
    console.error('Error al cargar perfil:', error);
    showAlert('danger', 'Error al cargar el perfil del usuario');
  } finally {
    setLoading(false);
  }
};

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showAlert('warning', 'Por favor seleccione un archivo de imagen válido');
        return;
      }
      
      if (file.size > 2 * 1024 * 1024) {
        showAlert('warning', 'El archivo es demasiado grande. Máximo 2MB permitido');
        return;
      }

      setAvatarFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewAvatar(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return userData.avatar;

    try {
      setUploadingAvatar(true);
      
      // Eliminar avatar anterior si existe
      if (userData.avatar) {
        try {
          const oldAvatarRef = ref(storage, userData.avatar);
          await deleteObject(oldAvatarRef);
        } catch (error) {
          console.log('No se pudo eliminar el avatar anterior:', error);
        }
      }

      // Subir nuevo avatar
      const avatarRef = ref(storage, `avatars/avatar_${currentUser.uid}_${Date.now()}.${avatarFile.name.split('.').pop()}`);
      const snapshot = await uploadBytes(avatarRef, avatarFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error al subir avatar:', error);
      showAlert('danger', 'Error al subir la imagen de perfil');
      return userData.avatar;
    } finally {
      setUploadingAvatar(false);
    }
  };

const handleSaveProfile = async () => {
  try {
    setSaving(true);

    if (!userData.name || !userData.apellidos || !userData.email) {
      showAlert('warning', 'Nombre, apellidos y email son campos obligatorios');
      return;
    }

    // Subir avatar si hay uno nuevo
    let avatarURL = userData.avatar;
    if (avatarFile) {
      avatarURL = await uploadAvatar();
    }

    // Solo actualizar campos de perfil, NUNCA rol/permisos/activo/dni
    // (rol se gestiona desde PermisosRoles, no desde el perfil)
    const { rol: _rol, permisos: _perm, activo: _act, dni: _dni, id: _id, documentId: _docId, ...safeFields } = userData;
    const updatedData = {
      ...safeFields,
      avatar: avatarURL,
      lastActive: new Date().toISOString()
    };

    // Determinar qué ID del documento usar
    let docId = currentUser.uid; // Por defecto usar UID
    
    if (userData.documentId) {
      // Si tenemos un documentId guardado (significa que se encontró por email/DNI)
      docId = userData.documentId;
    } else if (userData.dni && userData.dni !== '') {
      // Si tiene DNI y no tenemos documentId, verificar si existe un doc con ese DNI
      try {
        const dniDoc = await getDoc(doc(db, 'users', userData.dni));
        if (dniDoc.exists()) {
          docId = userData.dni;
        }
      } catch (error) {
        console.log('No se pudo verificar documento por DNI:', error);
      }
    }

    // Actualizar el documento
    await updateDoc(doc(db, 'users', docId), updatedData);

    // Si estamos usando un ID diferente al UID y el documento original no existe,
    // crear también una referencia con el UID para consistencia futura
    if (docId !== currentUser.uid) {
      try {
        const uidDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!uidDoc.exists()) {
          const { setDoc } = await import('firebase/firestore');
          await setDoc(doc(db, 'users', currentUser.uid), updatedData);
          showAlert('info', 'Se ha creado una referencia adicional para consistencia.');
        }
      } catch (error) {
        console.log('No se pudo crear referencia por UID:', error);
      }
    }

    // Actualizar email en Authentication si cambió
    if (userData.email !== currentUser.email) {
      await updateEmail(currentUser, userData.email);
    }

    setUserData(updatedData);
    setIsEditing(false);
    setAvatarFile(null);
    showAlert('success', 'Perfil actualizado correctamente');
    
  } catch (error) {
    console.error('Error al guardar perfil:', error);
    if (error.code === 'auth/requires-recent-login') {
      showAlert('warning', 'Debe volver a iniciar sesión para actualizar el email');
    } else {
      showAlert('danger', 'Error al guardar el perfil');
    }
  } finally {
    setSaving(false);
  }
};

  const handleChangePassword = async () => {
    try {
      if (!passwordData.currentPassword || !passwordData.newPassword) {
        showAlert('warning', 'Debe completar todos los campos de contraseña');
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        showAlert('warning', 'Las contraseñas nuevas no coinciden');
        return;
      }

      if (passwordData.newPassword.length < 6) {
        showAlert('warning', 'La nueva contraseña debe tener al menos 6 caracteres');
        return;
      }

      setChangingPassword(true);

      // Reautenticar usuario
      const credential = EmailAuthProvider.credential(currentUser.email, passwordData.currentPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Cambiar contraseña
      await updatePassword(currentUser, passwordData.newPassword);

      // Resolver ID de documento para limpiar el flag obligatorio
      let docId = currentUser.uid;
      if (userData.documentId) {
        docId = userData.documentId;
      } else if (userData.dni && userData.dni !== "") {
        docId = userData.dni;
      }

      await updateDoc(doc(db, "users", docId), {
        mustChangePassword: false,
        passwordChangedAt: new Date().toISOString()
      });

      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      if (!forcePasswordChange) {
        setShowPasswordForm(false);
      }
      showAlert('success', 'Contraseña actualizada correctamente');
      if (typeof onPasswordChanged === "function") {
        onPasswordChanged();
      }

    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      if (error.code === 'auth/wrong-password') {
        showAlert('danger', 'La contraseña actual es incorrecta');
      } else {
        showAlert('danger', 'Error al cambiar la contraseña');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setAvatarFile(null);
    setPreviewAvatar(userData.avatar || '');
    loadUserProfile();
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const formatLastActive = (dateString) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'success':
        return <FaCheckCircle className="me-2" />;
      case 'warning':
        return <FaExclamationTriangle className="me-2" />;
      case 'danger':
        return <FaTimesCircle className="me-2" />;
      case 'info':
        return <FaInfoCircle className="me-2" />;
      default:
        return <FaInfoCircle className="me-2" />;
    }
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          {/* Alerta Bootstrap */}
          {alert.show && (
            <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
              {getAlertIcon(alert.type)}
              {alert.message}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setAlert({ show: false, type: '', message: '' })}
                aria-label="Close"
              ></button>
            </div>
          )}

          {/* Header del perfil */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-primary text-white">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <FaUser className="me-2" />
                  Mi Perfil
                </h4>
                <div>
                  {!isEditing ? (
                    <div className="btn-group">
                      <button
                        className="btn btn-light btn-sm"
                        onClick={() => setIsEditing(true)}
                      >
                        <FaEdit className="me-1" />
                        Editar Perfil
                      </button>
                      <button
                        className="btn btn-outline-light btn-sm"
                        disabled={forcePasswordChange}
                        onClick={() => setShowPasswordForm(!showPasswordForm)}
                      >
                        <FaKey className="me-1" />
                        Cambiar Contraseña
                      </button>
                    </div>
                  ) : (
                    <div className="btn-group">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleSaveProfile}
                        disabled={saving || uploadingAvatar}
                      >
                        {saving ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                            Guardando...
                          </>
                        ) : (
                          <>
                            <FaSave className="me-1" />
                            Guardar
                          </>
                        )}
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleCancel}
                        disabled={saving || uploadingAvatar}
                      >
                        <FaTimes className="me-1" />
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>
              </div> 
            </div>

            <div className="card-body">
              <div className="row">
                {/* Avatar y estado */}
                <div className="col-lg-3 col-md-4 mb-4">
                  <div className="text-center">
                    <div className="position-relative d-inline-block mb-3">
                      {previewAvatar ? (
                        <img
                          src={previewAvatar}
                          alt="Avatar"
                          className="rounded-circle border"
                          style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div 
                          className="rounded-circle border d-flex align-items-center justify-content-center bg-light"
                          style={{ width: '150px', height: '150px' }}
                        >
                          <FaUser size={60} className="text-muted" />
                        </div>
                      )}
                      
                      {isEditing && (
                        <label 
                          className="btn btn-primary btn-sm position-absolute bottom-0 end-0 rounded-circle"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <FaCamera />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleAvatarChange}
                            className="d-none"
                            disabled={uploadingAvatar}
                          />
                        </label>
                      )}
                    </div>

                    <h5>{userData.name} {userData.apellidos}</h5>
                    <p className="text-muted mb-2">{userData.cargo}</p>
                    
                    <div className="d-flex justify-content-center align-items-center mb-2">
                      {userData.activo ? (
                        <span className="badge bg-success">
                          <FaCheckCircle className="me-1" />
                          Activo
                        </span>
                      ) : (
                        <span className="badge bg-danger">
                          <FaTimesCircle className="me-1" />
                          Inactivo
                        </span>
                      )}
                    </div>

                    <small className="text-muted">
                      <FaClock className="me-1" />
                      Último acceso: {formatLastActive(userData.lastActive)}
                    </small>
                  </div>
                </div>

                {/* Información personal */}
                <div className="col-lg-9 col-md-8">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        <FaUser className="me-1" />
                        Nombres *
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={userData.name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Ingrese sus nombres"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">Apellidos *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="apellidos"
                        value={userData.apellidos}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Ingrese sus apellidos"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        <FaEnvelope className="me-1" />
                        Email *
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={userData.email}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Ingrese su email"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        <FaIdCard className="me-1" />
                        DNI
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="dni"
                        value={userData.dni}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Ingrese su DNI"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        <FaBriefcase className="me-1" />
                        Cargo
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="cargo"
                        value={userData.cargo}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Ingrese su cargo"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        <FaShieldAlt className="me-1" />
                        Rol del Sistema
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={roles[userData.rol] || userData.rol}
                        disabled={true}
                        placeholder="Rol no asignado"
                      />
                      <small className="text-muted">El rol solo puede ser cambiado por un administrador</small>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        <FaBuilding className="me-1" />
                        RUC Empresa
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={userData.rucEmpresa}
                        disabled={true}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        <FaUserTie className="me-1" />
                        Supervisor
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={userData.supervisor}
                        disabled={true}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-bold">Dirección</label>
                      <textarea
                        className="form-control"
                        name="direccion"
                        value={userData.direccion || ''}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        rows="2"
                        placeholder="Ingrese su dirección"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Formulario de cambio de contraseña */}
          {showPasswordForm && (
            <div className="card shadow-sm">
              <div className="card-header bg-warning text-dark">
                <h5 className="mb-0">
                  <FaKey className="me-2" />
                  Cambiar Contraseña
                </h5>
              </div>
              <div className="card-body">
                {forcePasswordChange && (
                  <div className="alert alert-warning" role="alert">
                    Debes cambiar tu contraseña para continuar usando el sistema.
                  </div>
                )}
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label fw-bold">Contraseña Actual *</label>
                    <div className="input-group">
                      <input
                        type={showPasswords.current ? "text" : "password"}
                        className="form-control"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        placeholder="Ingrese su contraseña actual"
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => togglePasswordVisibility('current')}
                      >
                        {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">Nueva Contraseña *</label>
                    <div className="input-group">
                      <input
                        type={showPasswords.new ? "text" : "password"}
                        className="form-control"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        placeholder="Ingrese su nueva contraseña"
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => togglePasswordVisibility('new')}
                      >
                        {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">Confirmar Nueva Contraseña *</label>
                    <div className="input-group">
                      <input
                        type={showPasswords.confirm ? "text" : "password"}
                        className="form-control"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        placeholder="Confirme su nueva contraseña"
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => togglePasswordVisibility('confirm')}
                      >
                        {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-success"
                        onClick={handleChangePassword}
                        disabled={changingPassword}
                      >
                        {changingPassword ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                            Cambiando...
                          </>
                        ) : (
                          <>
                            <FaKey className="me-1" />
                            Cambiar Contraseña
                          </>
                        )}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          if (forcePasswordChange) return;
                          setShowPasswordForm(false);
                          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        }}
                        disabled={changingPassword || forcePasswordChange}
                      >
                        Cancelar
                      </button>
                    </div>
                    <small className="text-muted mt-2 d-block">
                      La contraseña debe tener al menos 6 caracteres
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Perfil;
